import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import {
  lookupYjElectrical,
  getMqttMappingByMeter,
  mqttPublishByMeter,
  getMqttLatestMessage,
} from '../../api';
import { adminRemoteRead51 } from '../../utils/yjMeterRemote';

const { Text } = Typography;

const fmtNum = (v, digits = 3) =>
  v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toFixed(digits);

const meterStatText = (v) => {
  if (v === 0 || v === '0') return '正常';
  if (v === 1 || v === '1') return '过载';
  return '-';
};

const sourceTitle = (src) => {
  if (src === 'heartbeat_54') return '最新 54 心跳（mqtt_heartbeat_raw）';
  if (src === 'latest_state') return '设备最新态原始帧（device_latest_state）';
  if (src === 'remote_51') return '本次远程 51 抄表';
  return src || '解析结果';
};

const ElectricalCard = ({ title, rawHex, receivedAt, parse, extra }) => {
  const p = parse || {};
  const frame = p.frame_type || extra?.frame || '-';
  return (
    <Card title={title} size="small" style={{ marginBottom: 16 }}>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="帧类型">{frame}</Descriptions.Item>
        <Descriptions.Item label="解析">
          {p.parse_ok ? <Tag color="success">完整</Tag> : <Tag color="warning">不完整/失败</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="剩余(度)">{fmtNum(p.remain ?? extra?.remain)}</Descriptions.Item>
        <Descriptions.Item label="总用(度)">{fmtNum(p.total ?? extra?.total)}</Descriptions.Item>
        <Descriptions.Item label="功率(W)">{fmtNum(p.power_w ?? extra?.powerW)}</Descriptions.Item>
        <Descriptions.Item label="电压(V)">{fmtNum(p.voltage_v ?? extra?.voltageV)}</Descriptions.Item>
        <Descriptions.Item label="电流(A)">{fmtNum(p.current_a ?? extra?.currentA)}</Descriptions.Item>
        <Descriptions.Item label="开关闸">
          {p.switch_text || extra?.switchStatus || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="电表状态">{meterStatText(p.meter_stat)}</Descriptions.Item>
        <Descriptions.Item label="时间" span={2}>
          {receivedAt ? String(receivedAt).replace('T', ' ').slice(0, 23) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="raw_hex" span={2}>
          {rawHex ? <Text code copyable={{ text: rawHex }}>{rawHex}</Text> : '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

const YjElectricalQuery = () => {
  const [meterNo, setMeterNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [remoteRow, setRemoteRow] = useState(null);

  const runQuery = async () => {
    const q = String(meterNo || '').trim();
    if (!q) {
      message.warning('请输入当前表号或 MAC');
      return;
    }
    setLoading(true);
    setError('');
    setRemoteRow(null);
    try {
      const res = await lookupYjElectrical(q);
      if (!res?.success) {
        setError(res?.msg || '查询失败');
        setResult(null);
        return;
      }
      setResult(res.data || null);
      const n = (res.data?.sources || []).length;
      if (n === 0) {
        message.info('无心跳原始帧，也无 latest-state raw；可点「远程抄表」试一次 51');
      } else {
        message.success(`查到 ${n} 条可解析源`);
      }
    } catch (e) {
      setError(e?.response?.data?.msg || e?.message || '查询失败');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const runRemoteRead = async () => {
    const q = String(meterNo || '').trim();
    if (!q) {
      message.warning('请输入当前表号或 MAC');
      return;
    }
    setReading(true);
    setError('');
    try {
      let device = result?.device;
      if (!device || !result?.device_found) {
        const res = await lookupYjElectrical(q);
        if (res?.success) {
          setResult(res.data || null);
          device = res.data?.device;
        }
      }
      if (!device) {
        const hex = q.replace(/[:\-\s]/g, '');
        device = { current_meter_no: q, device_mac: hex };
      }
      const parsed = await adminRemoteRead51({
        device,
        getMqttMappingByMeter,
        mqttPublishByMeter,
        getMqttLatestMessage,
      });
      if (!parsed) {
        setError('已下发 51，但超时未收到抄表回包。请确认表在线且主题映射有效。');
        setRemoteRow(null);
        return;
      }
      setRemoteRow({
        source: 'remote_51',
        raw_hex: parsed.raw || '',
        received_at: new Date().toISOString(),
        parse: {
          frame_type: '51',
          remain: parsed.remain,
          total: parsed.total,
          power_w: parsed.powerW,
          voltage_v: parsed.voltageV,
          current_a: parsed.currentA,
          switch_text: parsed.switchStatus === '通电' ? '合闸' : parsed.switchStatus === '断电' ? '拉闸' : parsed.switchStatus,
          parse_ok: parsed.powerW != null && parsed.voltageV != null && parsed.currentA != null,
        },
      });
      message.success('远程抄表回包已解析');
    } catch (e) {
      setError(e?.response?.data?.msg || e?.message || '远程抄表失败');
    } finally {
      setReading(false);
    }
  };

  return (
    <div>
      <PageTitle title="YJ电气量测试" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="单表解析：剩余 / 总用 / 开关闸 / 电压 / 电流 / 功率"
        description="54 心跳与 51 抄表均在剩余、总用之后、闸位之前带 EMPWR/EMV/EMC（÷1000）。本页只展示、不落库。先查最新心跳；需要实时值再点远程抄表。"
      />

      <Card title="查询条件" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 640 }}>
          <div>
            <Text type="secondary">当前表号 / MAC</Text>
            <Input
              value={meterNo}
              onChange={(e) => setMeterNo(e.target.value)}
              placeholder="如 YJ0014434 或 8A9AC32CC9EB"
              disabled={loading || reading}
              onPressEnter={runQuery}
            />
          </div>
          <Space>
            <Button type="primary" loading={loading} onClick={runQuery}>
              查询最新心跳
            </Button>
            <Button loading={reading} onClick={runRemoteRead}>
              远程抄表（51）
            </Button>
          </Space>
        </Space>
      </Card>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      {result ? (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={4}>
            <Text>输入：{result.input_meter_no}</Text>
            <Text>
              设备：
              {result.device_found
                ? `ID ${result.device?.id} / ${result.device?.current_meter_no || '-'} / MAC ${result.device?.device_mac || '-'}`
                : '未在 device_info 中找到（将直接用输入值查心跳 / 下发）'}
            </Text>
            <Text type="secondary">匹配键：{(result.heartbeat_keys || []).join(', ') || '-'}</Text>
          </Space>
        </Card>
      ) : null}

      {remoteRow ? (
        <ElectricalCard
          title={sourceTitle(remoteRow.source)}
          rawHex={remoteRow.raw_hex}
          receivedAt={remoteRow.received_at}
          parse={remoteRow.parse}
        />
      ) : null}

      {(result?.sources || []).map((row, idx) => (
        <ElectricalCard
          key={`${row.source}-${idx}`}
          title={sourceTitle(row.source)}
          rawHex={row.raw_hex}
          receivedAt={row.received_at}
          parse={row.parse}
        />
      ))}
    </div>
  );
};

export default YjElectricalQuery;
